import { useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { BlogArticleBody } from "@/components/blog/BlogArticleBody";
import { BlogPostCard } from "@/components/blog/BlogPostCard";
import { ContactPageAurora } from "@/components/contact/ContactPageAurora";
import { Container, SectionShell } from "@/components/ui/primitives";
import { useApp } from "@/context/AppProviders";
import { useReveal } from "@/hooks/useReveal";
import { usePageSeo } from "@/hooks/usePageSeo";
import { fetchBlogTag } from "@/lib/blogApi";
import {
  consumeBlogBootstrap,
  invalidateBlogBootstrap,
  peekBlogBootstrap,
} from "@/lib/blogBootstrap";
import { clearPaintShell } from "@/lib/clearPaintShell";
import { blogListPath, blogPostPath, homePath, tagPagePath } from "@/lib/routes";
import { getTagServicePillar } from "@/lib/serviceLinks";
import { normalizeTagSlug } from "@/lib/slug";
import {
  breadcrumbJsonLd,
  collectionPageJsonLd,
  formatPageTitle,
  truncateDescription,
} from "@/lib/seo";
import type { BlogListItem, BlogTagPage } from "@/types/blog";

function matchingTagBootstrap(tagSlug: string, page: number) {
  const boot = peekBlogBootstrap();
  if (boot?.type === "tag" && boot.tagSlug === tagSlug && boot.page === page) {
    return boot;
  }
  return null;
}

export function TagPage() {
  const { tagSlug } = useParams<{ tagSlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { locale } = useApp();
  const isTr = locale === "tr";
  const activeTagSlug = tagSlug ? normalizeTagSlug(tagSlug) : "";
  const pageParam = searchParams.get("page");
  const page = Math.max(1, Number(pageParam ?? "1") || 1);
  const prevTagSlugRef = useRef(activeTagSlug);
  const tagSlugChanged = prevTagSlugRef.current !== activeTagSlug;
  const fetchPage = tagSlugChanged ? 1 : page;

  const seeded = activeTagSlug ? matchingTagBootstrap(activeTagSlug, fetchPage) : null;
  const [tag, setTag] = useState<BlogTagPage | null>(seeded?.tag ?? null);
  const [items, setItems] = useState<BlogListItem[]>(seeded?.items ?? []);
  const [totalPages, setTotalPages] = useState(seeded?.totalPages ?? 0);
  const [loading, setLoading] = useState(!seeded);
  const [error, setError] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const skipFetchRef = useRef(Boolean(seeded));

  useReveal([locale, fetchPage, activeTagSlug, loading, items.length]);

  useEffect(() => {
    if (!loading) clearPaintShell();
  }, [loading]);

  useEffect(() => {
    prevTagSlugRef.current = activeTagSlug;
  }, [activeTagSlug]);

  useEffect(() => {
    if (!activeTagSlug) return;
    if (tagSlugChanged && pageParam) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete("page");
          return next;
        },
        { replace: true }
      );
    }
  }, [activeTagSlug, tagSlugChanged, pageParam, setSearchParams]);

  useEffect(() => {
    if (!activeTagSlug) return;

    let cancelled = false;
    const targetPage = fetchPage;

    const boot = consumeBlogBootstrap();
    if (boot?.type === "tag" && boot.tagSlug === activeTagSlug && boot.page === targetPage) {
      setTag(boot.tag);
      setItems(boot.items);
      setTotalPages(boot.totalPages);
      setLoading(false);
      setError(false);
      setNotFound(false);
      skipFetchRef.current = false;
      return () => {
        cancelled = true;
      };
    }

    if (skipFetchRef.current) {
      skipFetchRef.current = false;
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    invalidateBlogBootstrap();

    async function load() {
      setLoading(true);
      setError(false);
      setNotFound(false);

      let result = await fetchBlogTag(activeTagSlug, { page: targetPage, limit: 12 });
      if (cancelled) return;

      if (result.ok && result.data) {
        let data = result.data;

        if (data.items.length === 0 && data.total > 0 && targetPage > 1) {
          if (pageParam) {
            setSearchParams(
              (prev) => {
                const next = new URLSearchParams(prev);
                next.delete("page");
                return next;
              },
              { replace: true }
            );
          }
          result = await fetchBlogTag(activeTagSlug, { page: 1, limit: 12 });
          if (cancelled) return;
          if (!result.ok || !result.data) {
            setTag(null);
            setItems([]);
            setTotalPages(0);
            setError(true);
            setLoading(false);
            return;
          }
          data = result.data;
        }

        setTag(data.tag);
        setItems(data.items);
        setTotalPages(data.totalPages);
      } else if (result.error === "not_found") {
        setTag(null);
        setItems([]);
        setTotalPages(0);
        setNotFound(true);
      } else {
        setTag(null);
        setItems([]);
        setTotalPages(0);
        setError(true);
      }

      setLoading(false);
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [activeTagSlug, fetchPage, pageParam, setSearchParams]);

  const heading = tag?.metaTitle || tag?.label || activeTagSlug;
  const description =
    tag?.metaDescription ||
    tag?.shortDescription ||
    (isTr
      ? `${heading} etiketiyle yayınlanmış blog yazıları.`
      : `Blog posts tagged with ${heading}.`);
  const canonicalPath = activeTagSlug ? tagPagePath(locale, activeTagSlug, page) : undefined;
  const servicePillar = activeTagSlug ? getTagServicePillar(activeTagSlug) : null;
  const prevPath = page > 1 && activeTagSlug ? tagPagePath(locale, activeTagSlug, page - 1) : undefined;
  const nextPath =
    page < totalPages && activeTagSlug ? tagPagePath(locale, activeTagSlug, page + 1) : undefined;

  usePageSeo(
    useMemo(() => {
      if (!tag || !canonicalPath) return null;
      return {
        title: formatPageTitle(tag.metaTitle || tag.label),
        description: truncateDescription(description),
        canonicalPath,
        locale,
        robots: tag.noindex ? "noindex, follow" : "index, follow",
        publisher: "Enabase",
        prevPath,
        nextPath,
        jsonLd: [
          breadcrumbJsonLd([
            { name: isTr ? "Ana Sayfa" : "Home", path: homePath(locale) },
            { name: "Blog", path: blogListPath(locale) },
            { name: tag.label, path: canonicalPath },
          ]),
          collectionPageJsonLd({
            name: heading,
            description,
            path: canonicalPath,
          }),
        ],
      };
    }, [canonicalPath, description, heading, isTr, locale, nextPath, prevPath, tag])
  );

  if (!activeTagSlug) {
    return <Navigate to={blogListPath(locale)} replace />;
  }

  if (!loading && notFound) {
    return (
      <div className="bg-[var(--bg)] pt-[72px] pb-16">
        <Container className="page-hero-inner">
          <p className="text-sm text-[var(--text-md)]">
            {isTr ? "Etiket bulunamadı." : "Tag not found."}{" "}
            <Link to={blogListPath(locale)} className="font-semibold text-accent-link no-underline">
              {isTr ? "Bloga dön" : "Back to blog"}
            </Link>
          </p>
        </Container>
      </div>
    );
  }

  return (
    <>
      <section className="relative overflow-hidden bg-[var(--bg2)] pt-[72px]">
        <ContactPageAurora />
        <Container className="relative page-hero-inner pb-8 sm:pb-10 lg:pb-12">
          <nav className="mb-5 text-sm text-[var(--text-sm)] sm:mb-6" aria-label="Breadcrumb">
            <ol className="flex min-w-0 flex-wrap items-center gap-1.5">
              <li className="shrink-0">
                <Link to={homePath(locale)} className="no-underline transition hover:text-[var(--text)]">
                  {isTr ? "Ana Sayfa" : "Home"}
                </Link>
              </li>
              <li aria-hidden>/</li>
              <li className="shrink-0">
                <Link to={blogListPath(locale)} className="no-underline transition hover:text-[var(--text)]">
                  Blog
                </Link>
              </li>
              {tag ? (
                <>
                  <li aria-hidden>/</li>
                  <li>
                    <span className="font-medium text-[var(--text)]">{tag.label}</span>
                  </li>
                </>
              ) : null}
            </ol>
          </nav>

          <div className="max-w-2xl">
            <h1 className="linear-section-title text-balance">{heading}</h1>
            {description ? <p className="linear-body mt-3 max-w-lg">{description}</p> : null}
            {tag && tag.blogCount > 0 ? (
              <p className="mt-3 text-sm text-[var(--text-md)]">
                {isTr ? `${tag.blogCount} yazı` : `${tag.blogCount} posts`}
              </p>
            ) : null}
          </div>
        </Container>
      </section>

      <SectionShell variant="default" className="pt-0 pb-16 sm:pb-20 lg:pb-24">
        <Container>
          {servicePillar ? (
            <div className="fade-up mb-10 rounded-2xl border border-[var(--border)] bg-[var(--card)] p-5 sm:p-6">
              <p className="text-xs font-semibold uppercase tracking-[0.08em] text-[var(--text-sm)]">
                {isTr ? "İlgili Enabase modülü" : "Related Enabase module"}
              </p>
              <h2 className="mt-2 text-lg font-semibold tracking-tight text-[var(--text)] sm:text-xl">
                <Link to={servicePillar.href} className="no-underline transition hover:text-accent-link">
                  {servicePillar.label}
                </Link>
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[var(--text-md)]">
                {servicePillar.description}
              </p>
            </div>
          ) : null}

          {loading ? (
            <p className="fade-up py-16 text-center text-sm text-[var(--text-md)]">
              {isTr ? "Yazılar yükleniyor…" : "Loading posts…"}
            </p>
          ) : null}

          {!loading && error ? (
            <p className="fade-up rounded-xl border border-red-500/25 bg-red-500/10 px-4 py-6 text-center text-sm text-red-700 dark:text-red-300">
              {isTr
                ? "Etiket sayfası yüklenemedi. Veritabanı bağlantısını kontrol edin."
                : "Could not load tag page. Please check the database connection."}
            </p>
          ) : null}

          {!loading && !error && items.length === 0 ? (
            <p className="fade-up py-16 text-center text-sm text-[var(--text-md)]">
              {isTr ? "Bu etikette henüz yayınlanmış yazı bulunmuyor." : "No published posts for this tag yet."}
            </p>
          ) : null}

          {!loading && !error && items.length > 0 ? (
            <>
              <h2 className="fade-up mb-6 text-lg font-semibold tracking-tight text-[var(--text)] sm:text-xl">
                {isTr ? "İlgili yazılar" : "Related posts"}
              </h2>
              <div className="fade-up grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((post, index) => (
                  <BlogPostCard
                    key={post.id}
                    post={post}
                    href={blogPostPath(locale, post.slug)}
                    variant="grid"
                    priority={index === 0}
                  />
                ))}
              </div>

              {totalPages > 1 ? (
                <div className="fade-up mt-10 flex items-center justify-center gap-3">
                  {page > 1 ? (
                    <Link
                      to={tagPagePath(locale, activeTagSlug, page - 1)}
                      className="rounded-xl border border-[var(--border)] px-4 py-2 text-sm font-semibold no-underline transition hover:border-enabase-blue/30 hover:text-accent-link"
                    >
                      {isTr ? "Önceki" : "Previous"}
                    </Link>
                  ) : (
                    <span className="rounded-xl border border-[var(--border)] px-4 py-2 text-sm font-semibold opacity-50">
                      {isTr ? "Önceki" : "Previous"}
                    </span>
                  )}
                  <span className="text-sm text-[var(--text-md)]">
                    {page} / {totalPages}
                  </span>
                  {page < totalPages ? (
                    <Link
                      to={tagPagePath(locale, activeTagSlug, page + 1)}
                      className="rounded-xl border border-[var(--border)] px-4 py-2 text-sm font-semibold no-underline transition hover:border-enabase-blue/30 hover:text-accent-link"
                    >
                      {isTr ? "Sonraki" : "Next"}
                    </Link>
                  ) : (
                    <span className="rounded-xl border border-[var(--border)] px-4 py-2 text-sm font-semibold opacity-50">
                      {isTr ? "Sonraki" : "Next"}
                    </span>
                  )}
                </div>
              ) : null}
            </>
          ) : null}

          {tag?.contentHtml ? (
            <div className="fade-up mx-auto mt-12 max-w-3xl border-t border-[var(--border)] pt-10 sm:mt-14">
              <BlogArticleBody contentHtml={tag.contentHtml} blocks={[]} />
            </div>
          ) : null}
        </Container>
      </SectionShell>
    </>
  );
}
